import { DAYS_PER_MONTH } from '../config/constants';
import { economyConfig } from '../config/economyConfig';
import { getDistrict } from '../data/districts';
import { getUpgrade, UPGRADE_LIST } from '../data/upgrades';
import type { Competitor, CompetitorLot } from '../models/Competitor';
import type { GameState } from '../models/GameState';
import type { GameTime } from '../models/GameTime';
import { clamp } from '../utils/math';
import type { RandomGenerator } from '../utils/random';
import { DemandEngine } from './DemandEngine';
import { MarketEngine } from './MarketEngine';

/**
 * CompetitorEngine (spec sections 20, 35): runs the AI companies under the SAME
 * economy rules as the player — no cheating. Daily it settles each competitor's
 * abstract P&L from its market share; monthly it makes strategic decisions
 * (price / upgrade / expand) bounded by its own cash. Pure & deterministic.
 */
export const CompetitorEngine = {
  /** Average hours a parked car stays (for abstract competitor revenue). */
  AVG_HOURS: 4,
  /** Times a space can turn over per day (throughput cap). */
  TURNOVER_PER_DAY: 3,
  /** Abstract daily operating cost per space (rent/maint/electric/staff proxy). */
  DAILY_COST_PER_SPACE: 220,

  // --- daily economics ---

  settleDaily(state: GameState, time: GameTime): void {
    for (const competitor of state.competitors) {
      let dayProfit = 0;
      let repSum = 0;
      for (const lot of competitor.lots) {
        dayProfit += this.lotDailyProfit(state, lot, time);
        repSum += lot.reputation;
      }
      competitor.cash += dayProfit;
      competitor.reputation =
        competitor.lots.length > 0 ? repSum / competitor.lots.length : competitor.reputation;
    }
  },

  /** Abstract daily profit for one competitor lot from its market share. */
  lotDailyProfit(state: GameState, lot: CompetitorLot, time: GameTime): number {
    const district = getDistrict(lot.districtId);
    if (!district) return 0;

    // District daily demand pool (sum of hourly pool over the day).
    let dailyPool = 0;
    for (let hour = 0; hour < 24; hour++) {
      dailyPool +=
        district.baseDemand *
        DemandEngine.timeMultiplierAtHour(district, hour) *
        DemandEngine.dayMultiplier(district, time) *
        economyConfig.DEMAND_MULTIPLIER;
    }

    // This lot's share of the district by attractiveness.
    const parts = MarketEngine.participantsIn(state, lot.districtId);
    const weights = parts.map((p) =>
      MarketEngine.attractiveness(p.price, p.reputation, p.capacity)
    );
    const total = weights.reduce((s, w) => s + w, 0);
    const mine = MarketEngine.attractiveness(lot.price, lot.reputation, lot.capacity);
    const share = total > 0 ? mine / total : 0;

    const arrivals = dailyPool * share;
    const throughput = lot.capacity * this.TURNOVER_PER_DAY;
    const served = Math.min(arrivals, throughput);

    const revenue = served * lot.price * this.AVG_HOURS;

    const rent = (economyConfig.BASE_RENT * district.rentMultiplier) / DAYS_PER_MONTH;
    let upgradeMaint = 0;
    for (const id of lot.upgrades) upgradeMaint += getUpgrade(id)?.maintenanceCost ?? 0;
    const expenses =
      rent + lot.capacity * this.DAILY_COST_PER_SPACE + upgradeMaint / DAYS_PER_MONTH;

    return revenue - expenses;
  },

  // --- monthly strategic decisions ---

  decideMonthly(state: GameState, rng: RandomGenerator): void {
    for (const competitor of state.competitors) {
      this.decideForCompetitor(competitor, state, rng);
    }
  },

  decideForCompetitor(
    competitor: Competitor,
    state: GameState,
    rng: RandomGenerator
  ): void {
    // 1) Nudge each lot's price toward the strategy target, reacting to share.
    for (const lot of competitor.lots) {
      const shares = MarketEngine.districtShares(state, lot.districtId);
      const mine = shares.find((s) => s.ownerId === competitor.id)?.share ?? 0;
      const step = economyConfig.PRICE_STEP;

      if (lot.price > competitor.priceTarget) lot.price -= step;
      else if (lot.price < competitor.priceTarget) lot.price += step;

      // Losing badly -> undercut a bit; dominating -> creep price up.
      if (mine < 0.2) lot.price -= step;
      else if (mine > 0.5) lot.price += step;

      lot.price = clamp(lot.price, economyConfig.PRICE_MIN, economyConfig.PRICE_MAX);
    }

    // 2) One investment per month if affordable and it fits the strategy.
    this.tryInvest(competitor, rng);
  },

  /** Spend cash on an upgrade or expansion — never more than the AI has. */
  tryInvest(competitor: Competitor, rng: RandomGenerator): void {
    if (competitor.lots.length === 0) return;
    // Pick a lot to invest in (deterministic-ish via rng).
    const lot = competitor.lots[rng.nextInt(0, competitor.lots.length - 1)];

    // Smart companies chase tech upgrades; premium chase service; budget expands.
    if (competitor.strategy === 'smart' || competitor.strategy === 'premium') {
      const wanted =
        competitor.strategy === 'smart'
          ? UPGRADE_LIST
          : ['cctv', 'ev_charger', 'automatic_barrier'];
      for (const id of wanted) {
        if (lot.upgrades.includes(id)) continue;
        const def = getUpgrade(id);
        if (!def) continue;
        // Respect prerequisites (same rule as the player).
        if (!def.prerequisites.every((p) => lot.upgrades.includes(p))) continue;
        if (competitor.cash >= def.cost) {
          competitor.cash -= def.cost;
          lot.upgrades.push(id);
          return;
        }
      }
    }

    // Budget (or if no upgrade bought): expand capacity when cash allows.
    const expandCost = economyConfig.EXPANSION_COST;
    if (
      competitor.cash >= expandCost &&
      rng.next() < competitor.riskTolerance
    ) {
      competitor.cash -= expandCost;
      lot.capacity += 20;
    }
  },
};
